// Pure, browser-free logic for the experimental recorder polish (phase B):
// press-and-hold gesture classification and a paused-time-excluding timer.
// Kept import-free so it runs under mocha/node and is unit-testable.

// --- press-and-hold gesture -------------------------------------------------

// ponytail: fixed thresholds (device-pixel deltas), not calibrated per screen
// DPI. Ceiling: on a very high-DPI touch panel 80px is a shorter physical
// slide; upgrade path = scale by a measured px-per-mm if it feels off. Up is
// negative dy, left is negative dx (pointer coordinate convention).
export const CANCEL_DX = -80 // slid this far left → discard
export const LOCK_DY = -60 // slid this far up → hands-free lock
export const TAP_MAX_MS = 300 // released this fast with no slide → tap-to-toggle

export type Gesture = 'cancel' | 'lock' | 'hold-release' | 'tap'

/**
 * Classify a completed (or in-progress) press-and-hold on the mic button.
 * dx/dy are pointer deltas from the press origin (up = negative dy, left =
 * negative dx); elapsedMs is how long the pointer was down.
 *
 * Cancel and lock win over the time test so a fast flick still cancels/locks.
 * Cancel is checked before lock so a diagonal down-left flick discards rather
 * than latching a recording the user was trying to throw away.
 */
export function classifyGesture(
  dx: number,
  dy: number,
  elapsedMs: number
): Gesture {
  if (dx <= CANCEL_DX) {
    return 'cancel'
  }
  if (dy <= LOCK_DY) {
    return 'lock'
  }
  if (elapsedMs < TAP_MAX_MS) {
    return 'tap'
  }
  return 'hold-release'
}

// --- paused-time-excluding timer -------------------------------------------

// Wall-clock accumulator that only counts time while running. Pausing banks the
// running segment; the display reads total banked + the current open segment.
// More correct than skipping setInterval ticks (no ±1s slop at each pause
// boundary), and trivially testable with an injected clock.
export type TimerState = { accumulatedMs: number; runningSince: number | null }

export function timerStart(now: number): TimerState {
  return { accumulatedMs: 0, runningSince: now }
}

export function timerPause(s: TimerState, now: number): TimerState {
  if (s.runningSince == null) {
    return s // already paused → no-op (idempotent)
  }
  return {
    accumulatedMs: s.accumulatedMs + (now - s.runningSince),
    runningSince: null,
  }
}

export function timerResume(s: TimerState, now: number): TimerState {
  if (s.runningSince != null) {
    return s // already running → no-op (idempotent)
  }
  return { accumulatedMs: s.accumulatedMs, runningSince: now }
}

export function timerElapsedMs(s: TimerState, now: number): number {
  return s.accumulatedMs + (s.runningSince != null ? now - s.runningSince : 0)
}

// --- advisory getUserMedia constraints -------------------------------------

/**
 * Whether an "original audio" request was actually honored, read back from
 * `track.getSettings()`. getSettings may omit these keys entirely (the mic /
 * browser doesn't expose them) → null = "can't tell / unsupported". When
 * present, honored only if every reported key is false (no processing applied).
 */
export function originalAudioHonored(
  settings: Record<string, unknown>
): boolean | null {
  const keys = ['noiseSuppression', 'echoCancellation', 'autoGainControl']
  const present = keys.filter(k => k in settings)
  if (present.length === 0) {
    return null
  }
  return present.every(k => settings[k] === false)
}

// --- microphone picker + inline no-sound warning ---------------------------

/**
 * Which deviceId to actually record with: the persisted one if it's still in
 * the enumerated inputs, else null (system default). A stale id would make
 * getUserMedia({ deviceId: { exact } }) throw OverconstrainedError; falling
 * back to null keeps the recording alive. Pure so it's unit-testable; start()
 * additionally catches the runtime race where a device vanishes mid-flight.
 */
export function resolveDeviceId(
  persisted: string | null,
  availableIds: string[]
): string | null {
  return persisted != null && availableIds.indexOf(persisted) !== -1
    ? persisted
    : null
}

/**
 * Display name for an audioinput. Empty labels happen before mic permission is
 * granted; here permission is already granted so labels populate and the
 * fallback is a rare edge case.
 * ponytail: English "Microphone N" fallback (not localized) — only surfaces for
 * empty labels, which don't occur post-permission. Localize if it ever shows.
 */
export function deviceLabel(
  label: string | null | undefined,
  index: number
): string {
  const l = (label ?? '').trim()
  return l !== '' ? l : `Microphone ${index + 1}`
}

// Continuous near-silence for this long → show the inline "no sound" hint.
// Threshold is a small RMS floor; real speech/room tone sits well above it.
// ponytail: fixed threshold, not noise-floor-calibrated per mic. Ceiling: a
// very quiet mic could false-warn; upgrade path = adaptive floor from the first
// second of signal.
export const SILENCE_RMS_THRESHOLD = 0.01
export const SILENCE_HOLD_MS = 3000

export type SilenceState = { quietSinceMs: number | null }

export function silenceReset(): SilenceState {
  return { quietSinceMs: null }
}

/**
 * Feed (rmsValue, nowMs) once per audio frame. Any signal above the threshold
 * resets the quiet timer; sustained near-silence accumulates and reports true
 * after SILENCE_HOLD_MS. Paused time can't count toward silence because the
 * recorder skips frames while paused (nothing is fed) — reset on resume so the
 * paused gap doesn't back-date quietSince.
 */
export function silenceStep(
  s: SilenceState,
  rms: number,
  nowMs: number
): [SilenceState, boolean] {
  if (rms > SILENCE_RMS_THRESHOLD) {
    return [{ quietSinceMs: null }, false]
  }
  const quietSince = s.quietSinceMs ?? nowMs
  return [{ quietSinceMs: quietSince }, nowMs - quietSince >= SILENCE_HOLD_MS]
}
